import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';

const routes = (window.Theme && window.Theme.routes) || {};
const ADD_URL = routes.cart_add_url || '/cart/add.js';
const CART_URL = routes.cart_url || '/cart';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function money(cents) {
  const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: cur }).format(cents / 100);
  } catch (e) {
    return '£' + (cents / 100).toFixed(2);
  }
}

/** Adds a variant through Helio's pipeline. `source` must be an element in the page (or in a dialog). */
export async function cloAddToCart(variantId, quantity, source) {
  const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId).filter(Boolean);
  if (document.querySelector('[data-cl-bag-dialog]')) sectionIds.push('cl-bag-confirmation');
  const body = new FormData();
  body.append('id', String(variantId)); body.append('quantity', String(quantity || 1));
  body.append('sections_url', location.pathname);
  if (sectionIds.length) body.append('sections', [...new Set(sectionIds)].slice(0, 5).join(','));
  const deferred = CartLinesUpdateEvent.createPromise();
  (source || document).dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(variantId), quantity: quantity || 1 }], promise: deferred.promise }));
  let result;
  try {
    const response = await fetch(ADD_URL, { method: 'POST', body, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, signal: AbortSignal.timeout(25000) });
    result = await response.json();
    if (!response.ok || result.status || String(result.variant_id || result.id) !== String(variantId)) throw new Error(result.description || result.message || document.querySelector('[data-cl-bag-dialog]')?.dataset.failedLabel);
  } catch (error) {
    (source || document).dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID', detail: { description: error.message } }));
    if (deferred.reject) deferred.reject(error); else deferred.resolve({ detail: { didError: true } });
    throw error;
  }
  const synchronize = async () => {
   try {
    const response = await fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Bag count unavailable');
    const cart = await response.json();
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'cavendis-quick-add', sourceId: 'cavendis', itemCount: quantity || 1, sections: result.sections, didError: false } });
  } catch (error) {
    if (deferred.reject) deferred.reject(error); else deferred.resolve({ detail: { didError: true } });
   }
  };
  void synchronize();
  return result;
}
window.cloAddToCart = cloAddToCart;

let bagDialog;
let bagFocus;
function confirmation() {
  const element = document.querySelector('[data-cl-bag-dialog]');
  if (!element || element === bagDialog) return element;
  bagDialog = element;
  element.querySelectorAll('[data-cl-bag-close]').forEach(button => button.addEventListener('click', () => element.close()));
  element.addEventListener('click', event => {
    if (event.target !== element) return;
    const rect = element.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) element.close();
  });
  element.addEventListener('close', () => {
    document.documentElement.classList.remove('clp-bag-open');
    unlockScroll(element);
    if (bagFocus?.isConnected && !bagFocus.disabled) bagFocus.focus({ preventScroll: true });
  });
  return element;
}
function showConfirmation() {
  window.location.assign(CART_URL);
}

window.CLBagConfirmation = { show: showConfirmation };

document.addEventListener('submit', async event => {
  const form = event.target.closest('[data-clo-product] form[action*="/cart/add"]');
  if (!form || event.submitter?.name === 'checkout') return;
  event.preventDefault();
  const input = form.querySelector('input[name="id"]'),section = form.closest('[data-clo-product]');
  const data = JSON.parse(section.querySelector('[data-clo-variants]').textContent);
  const selected = data.variants.find(variant => String(variant.id) === input.value);
  if (input.disabled || !selected?.available || form.dataset.clBusy === 'true' || section.dataset.clGalleryBusy === 'true' || !form.reportValidity()) return;
  const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
  const galleryData = JSON.parse(section.querySelector('[data-clo-gallery]').textContent);
  const colour = data.colourIndex > -1 ? selected.options[data.colourIndex] : '';
  const photo = window.CLPremiumGallery.resolve(galleryData, selected.id, colour).images[0]?.url;
  const snapshot = { colour, photo, heading: section.querySelector('[data-clo-title]')?.cloneNode(true), focus: event.submitter || section.querySelector('.clo-atb[type="submit"]') };
  form.dataset.clBusy = 'true';
  const buttons = [...section.querySelectorAll('.clo-atb'), ...document.querySelectorAll('[data-cl-product-owner] .clo-atb')];
  buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
  const errorBox = section.querySelector('.clo-buy__err'); if (errorBox) errorBox.textContent = '';
  try {
    const item = await cloAddToCart(selected.id, quantity, form);
    showConfirmation(item, quantity, snapshot);
  } catch (error) {
    if (errorBox) errorBox.textContent = error.name === 'TypeError' || error.name === 'TimeoutError' ? confirmation()?.dataset.failedLabel : error.message;
  } finally {
    form.dataset.clBusy = 'false';
    const current = data.variants.find(variant => String(variant.id) === input.value);
    buttons.forEach(button => { button.disabled = !current?.available || section.dataset.clGalleryBusy === 'true'; button.removeAttribute('aria-busy'); });
  }
});
